import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';

import {
  ResourceFilters,
  TAGS_PARAM,
  TINY_PLUS_PARAM,
  parseTags,
} from '../VideoResources/VideoResources.helpers';

type ChangedField = 'tag' | 'tiny_plus_only';

// URL-synced filter state shared by the webinars and guides sections.
// `countMatches` is injected so the caller totals matches for the analytics event.
export const useContentFilters = (
  countMatches: (filters: ResourceFilters) => number,
) => {
  const { trackEvent } = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTags, setSelectedTags] = useState<string[]>(() =>
    parseTags(searchParams.get(TAGS_PARAM) ?? ''),
  );
  const [tinyPlusOnly, setTinyPlusOnly] = useState<boolean>(
    () => searchParams.get(TINY_PLUS_PARAM) === 'true',
  );

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (selectedTags.length) {
      next.set(TAGS_PARAM, selectedTags.join(','));
    } else {
      next.delete(TAGS_PARAM);
    }
    if (tinyPlusOnly) {
      next.set(TINY_PLUS_PARAM, 'true');
    } else {
      next.delete(TINY_PLUS_PARAM);
    }
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [selectedTags, tinyPlusOnly, searchParams, setSearchParams]);

  const filters = useMemo<ResourceFilters>(
    () => ({ tags: selectedTags, tinyPlusOnly }),
    [selectedTags, tinyPlusOnly],
  );

  const fireFilterEvent = (next: ResourceFilters, changed: ChangedField) => {
    trackEvent('Resources Filtered', {
      tags: next.tags,
      tiny_plus_only: next.tinyPlusOnly,
      changed,
      result_count: countMatches(next),
    });
  };

  const toggleTag = (tag: string) => {
    const tags = selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    setSelectedTags(tags);
    fireFilterEvent({ tags, tinyPlusOnly }, 'tag');
  };

  const toggleTinyPlusOnly = (on: boolean) => {
    setTinyPlusOnly(on);
    fireFilterEvent({ tags: selectedTags, tinyPlusOnly: on }, 'tiny_plus_only');
  };

  const isFiltering = selectedTags.length > 0 || tinyPlusOnly;

  return { filters, toggleTag, toggleTinyPlusOnly, isFiltering };
};
